import { Circle } from '@phosphor-icons/react';
import { ICellRendererParams } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';

import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { TextLine, Tooltip } from 'core/elements';
import { getAgGridTableCellData } from 'core/utils';
import { tradeAgreementStatusColorMap } from 'quotes/constants';
import { TradeAgreementStatus } from 'quotes/enums';
import { VendorQuoteHelper } from 'quotes/helpers';
import { Color, TooltipPosition } from 'theme/enums';

// TextLine inherits .ag-cell's justify-content: center, which centers it horizontally.
const leftAlignSx = { justifyContent: 'flex-start' };

const TradeAgreementStatusCell = ({ ...props }: ICellRendererParams) => {
  const { t } = useTranslation();

  const data = getAgGridTableCellData(props, 'tradeAgreementStatus');
  if (!data) {
    return props?.value;
  }

  const status = data.tradeAgreementStatus as TradeAgreementStatus | null | undefined;
  // The BE sends NULL for a VQ that cannot be sent as SPA (not imported /
  // editable_by_customer) — a Pending chip there would be misleading.
  if (!status) {
    return <TextLine text={DEFAULT_PLACEHOLDER} color={Color.NEUTRAL} sx={leftAlignSx} />;
  }

  const error = data.tradeAgreementError as string | null;

  // Values are already display-ready (BE casing), so render them directly.
  const statusLine = (
    <TextLine
      text={status}
      color={tradeAgreementStatusColorMap[status] ?? Color.NEUTRAL}
      decorator={<Circle weight="fill" style={{ fontSize: 10 }} />}
      sx={leftAlignSx}
    />
  );

  // Only a Failed send carries a persisted error; surface it verbatim on hover.
  if (status === TradeAgreementStatus.FAILED && error) {
    return (
      <Tooltip title={error} color={Color.DANGER} placement={TooltipPosition.TOP}>
        {statusLine}
      </Tooltip>
    );
  }

  // The label alone doesn't say what changed or that nothing was re-sent.
  if (TradeAgreementStatus.UPDATED_POST_SENT === status) {
    const tooltip = VendorQuoteHelper.getUpdatedPostSentTooltip(
      data.tradeAgreementSentVersion as number | null | undefined
    );

    return (
      <Tooltip
        title={t(tooltip.key, tooltip.options)}
        color={Color.WARNING}
        placement={TooltipPosition.TOP}
      >
        {statusLine}
      </Tooltip>
    );
  }

  return statusLine;
};

export default TradeAgreementStatusCell;
